import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Select, Table } from 'antd';
import styled from 'styled-components';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import { Label } from '~/pages/common.css';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import PersonalAccessTokenService from '~/modules/services/personal-access-token-service';
import ApiTokenRevealModal from './api-token-reveal-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['personalAccessTokens'];

const EXPIRY_OPTIONS = [
	{ label: '30 days', value: 30 },
	{ label: '60 days', value: 60 },
	{ label: '90 days', value: 90 },
	{ label: '180 days', value: 180 },
	{ label: '1 year', value: 365 }
];

const ApiTokensContainer = styled.div`
	min-height: fit-content;

	fieldset {
		margin-bottom: 2rem;
	}

	.warning-copy {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 1rem;
	}

	.create-form {
		display: flex;
		gap: 0.75rem;
		align-items: flex-end;
		margin-bottom: 1.5rem;
		flex-wrap: wrap;
	}

	.create-form > div {
		display: flex;
		flex-direction: column;
	}

	.create-form .name-field {
		flex: 1 1 16rem;
	}

	.create-form .expiry-field {
		flex: 0 0 10rem;
	}
`;

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const expiresAtFromDays = (days) => Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;

const ApiTokens = () => {
	const queryClient = useQueryClient();

	const [name, setName] = useState('');
	const [expiryDays, setExpiryDays] = useState(90);
	const [revealedToken, setRevealedToken] = useState(null);

	const { data: tokens = [], isLoading } = useQuery(
		QUERY_KEY,
		() => PersonalAccessTokenService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: createToken, isLoading: isCreating } = useMutation(
		({ name, expiresAt }) => PersonalAccessTokenService.create({ name, expiresAt }),
		{
			onSuccess: (token) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedToken(token);
				setName('');
				setExpiryDays(90);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to create token';
				ToastService.create('error', message);
				captureError(err, `Error creating personal access token: ${err.message}`);
			}
		}
	);

	const { mutateAsync: revokeToken } = useMutation(
		(id) => PersonalAccessTokenService.revoke(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
				ToastService.create('success', 'Token revoked');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to revoke token');
				captureError(err, `Error revoking personal access token: ${err.message}`);
			}
		}
	);

	const handleCreate = async() => {
		const trimmed = name.trim();
		if(!trimmed) {
			return;
		}
		await createToken({ name: trimmed, expiresAt: expiresAtFromDays(expiryDays) });
	};

	const handleRevoke = async(row) => {
		const ok = await confirm({
			title: 'Revoke API Token',
			content: `Revoke "${row.name}"? Anything using this token will stop working.`,
			confirmText: 'Revoke',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await revokeToken(row._id);
	};

	const columns = [
		{ title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
		{
			title: 'Token',
			dataIndex: 'tokenPrefix',
			key: 'tokenPrefix',
			render: (prefix) => <code>pat_{prefix}…</code>
		},
		{ title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: formatDate },
		{ title: 'Last used', dataIndex: 'lastUsedAt', key: 'lastUsedAt', render: formatDate },
		{ title: 'Expires', dataIndex: 'expiresAt', key: 'expiresAt', render: formatDate },
		{
			title: 'Action',
			key: 'action',
			width: 140,
			render: (_value, row) => (
				<Button
					variant="danger"
					data-testid="api-token-revoke-button"
					onClick={() => handleRevoke(row)}
				>
					<Icon use="trash" color="white" /> Revoke
				</Button>
			)
		}
	];

	return (
		<ApiTokensContainer data-testid="preferences-api-tokens">
			<fieldset>
				<legend className="h5">Create API token</legend>
				<p className="warning-copy">
					API tokens act on your behalf with your full account permissions. Treat them like a password.
					You will see the token value once, when it is created.
				</p>
				<div className="create-form">
					<div className="name-field">
						<Label>Name</Label>
						<Input
							placeholder="e.g. ci-deploy"
							value={name}
							onChange={(e) => setName(e.target.value)}
							data-testid="api-token-name-input"
						/>
					</div>
					<div className="expiry-field">
						<Label>Expires in</Label>
						<Select
							style={{ width: '100%' }}
							value={expiryDays}
							onChange={setExpiryDays}
							options={EXPIRY_OPTIONS}
							data-testid="api-token-expiry-select"
						/>
					</div>
					<div>
						<Button
							onClick={handleCreate}
							loading={isCreating}
							disabled={!name.trim() || isCreating}
							data-testid="api-token-create-button"
						>
							Create token
						</Button>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<legend className="h5">Active tokens</legend>
				<TableStyles>
					<Table
						columns={columns}
						dataSource={tokens.map((t) => ({ ...t, key: t._id }))}
						pagination={false}
						loading={isLoading}
						locale={{ emptyText: <EmptyState message="No API tokens" /> }}
					/>
				</TableStyles>
			</fieldset>
			<ApiTokenRevealModal
				token={revealedToken}
				onClose={() => setRevealedToken(null)}
			/>
		</ApiTokensContainer>
	);
};

export default ApiTokens;
